<script>
import PhoneNumber from "@/components/PhoneNumber.vue";
import LanguageTag from "@/components/status/LanguageTag.vue";
import { OrdersMixin } from "@/views/orders/orders.mixin";
import ShowMore from "@/components/ShowMore.vue";
import TravelOrderStatusLabel from "@/views/trips/components/TravelOrderStatus.vue";
import { RouteStatus, TravelOrderCancellationStatusMap } from "@/data/trip";
import AlertMessage from "@/components/AlertMessage.vue";
import { PlannedRouteMessage } from "@/data/route.js";
import CancelledChip from "@/components/chips/CancelledChip.vue";
import RouteMenu from "@/views/trips/view/components/RouteMenu.vue";
import ReplaceTruckDialog from "@/views/trips/view/dialogs/ReplaceTruckDialog.vue";
import TruckRoutesDialog from "@/components/truck/TruckRoutesDialog.vue";
import { API } from "@/api/api.ts";
import TruckLink from "@/components/links/TruckLink.vue";
import RateConfirmationDialog from "@/views/trips/view/dialogs/RateConfirmationDialog.vue";

const api = new API();

export default {
  name: "RouteInfo",
  components: {
    RateConfirmationDialog,
    TruckLink,
    RoutesDialog: TruckRoutesDialog,
    ReplaceTruckDialog,
    RouteMenu,
    RouteCancelledChip: CancelledChip,
    AlertMessage,
    TravelOrderStatusLabel,
    ShowMore,
    LanguageTag,
    PhoneNumber,
  },
  mixins: [OrdersMixin],
  props: {
    route: Object,
    tripId: Number,
  },
  emits: ["change", "success"],
  data() {
    return {
      expanded: true,
      showReplaceTruckDialog: false,
      showRateConfirmationDialog: false,
      replacedTruck: null,
      assignedTruck: null,
      trucksRoutes: [],
      showTrucksRoutesDialog: false,
    };
  },
  computed: {
    driverRate() {
      return this.route.travelOrders.reduce(
        (sum, travelOrder) => sum + (travelOrder.rate || travelOrder.dryRunRate),
        0,
      );
    },
    isPlanned() {
      return this.route.status === RouteStatus.planned;
    },
    PlannedRouteMessage() {
      return PlannedRouteMessage;
    },
    isCancelled() {
      return this.route.status === RouteStatus.cancelled;
    },
    routeInfoClass() {
      if (this.isCancelled) {
        return "bg-red-lighten-5";
      }

      return "bg-white";
    },
  },
  methods: {
    toggleExpanded() {
      this.expanded = !this.expanded;
    },
    isTravelOrderCancelled(travelOrder) {
      return TravelOrderCancellationStatusMap.some((s) => s.id === travelOrder.status);
    },
    configureTruckRoutes(truckRoutesInfo) {
      return truckRoutesInfo
        ? truckRoutesInfo.routes.map((route) => {
            return {
              ...route,
              truckNumber: truckRoutesInfo.truckNumber,
            };
          })
        : [];
    },
    async finalizeReplaceTruck(replacedTruck, assignedTruck) {
      this.$emit("change");

      this.replacedTruck = replacedTruck;
      this.assignedTruck = assignedTruck;

      try {
        const [replacedResp, assignedResp] = await Promise.all([
          api.trucks.getTruckRoutes(replacedTruck.id),
          api.trucks.getTruckRoutes(assignedTruck.id),
        ]);

        const replacedRoutes = this.configureTruckRoutes(replacedResp.data);

        const assignedRoutes = this.configureTruckRoutes(assignedResp.data);

        this.trucksRoutes = [...replacedRoutes, ...assignedRoutes];

        const hasEmptyMiles = this.trucksRoutes.some((route) => route.wrongEmptyMiles);

        this.showTrucksRoutesDialog = hasEmptyMiles;
      } catch (error) {
        console.error(error);
      }
    },
  },
};
</script>

<template>
  <div :class="routeInfoClass" class="rounded pa-4 mb-4">
    <div class="d-flex align-center justify-space-between mb-3">
      <div class="d-flex align-center">
        <div class="font-weight-500 text-grey-darken-2">Route</div>
        <v-chip v-if="isPlanned" class="bg-indigo-lighten-2 ml-3" density="comfortable" size="small">Planned</v-chip>
        <route-cancelled-chip v-if="isCancelled" class="ml-3" />
      </div>

      <route-menu
        v-model:show-rate-confirmation-dialog="showRateConfirmationDialog"
        v-model:show-replace-truck-dialog="showReplaceTruckDialog"
        :dispatch-confirmed="route.dispatchConfirmed"
      />
    </div>

    <div v-if="route.truck" class="rounded pa-4 bg-grey-lighten-4 position-relative">
      <div :class="{ expanded }" class="expand-button" @click="toggleExpanded">
        <v-icon color="primary" icon="mdi-chevron-down" size="16"></v-icon>
      </div>
      <div class="d-flex align-center">
        <router-link
          :to="{ name: 'truck-view', params: { id: route.truck.id } }"
          class="text-primary text-decoration-none font-weight-500"
          data-qa="route-truck-number"
          target="_blank"
        >
          Truck {{ route.truck.number }}
        </router-link>
        <div class="font-weight-500 font-size-12 ml-3 text-grey-darken-2" data-qa="route-total-driver-rate">
          Total driver rate: {{ driverRate }}$
        </div>
      </div>
      <div v-if="expanded">
        <div v-if="route.driver" class="d-flex align-center mt-3 pb-4 border-b">
          <div
            class="rounded-circle text-uppercase text-white driver-label bg-indigo-lighten-2"
            data-qa="route-driver-label"
          >
            dr
          </div>
          <router-link
            :to="{ name: 'user-view', params: { id: route.driver.id } }"
            class="text-primary text-decoration-none ml-2"
            data-qa="route-driver-name"
            target="_blank"
          >
            {{ route.driver.name }}
          </router-link>
          <phone-number
            :number="route.driver.phone"
            class="text-primary ml-2"
            data-qa="route-driver-phone"
          ></phone-number>
          <language-tag :language="route.driver.language" class="ml-3" light></language-tag>
        </div>
        <div v-for="travelOrder in route.travelOrders" :key="travelOrder.id" class="mt-4">
          <template v-if="travelOrder.order">
            <div class="d-flex justify-space-between">
              <div class="d-flex align-center">
                <div class="text-primary font-size-12 font-weight-500" data-qa="route-travel-order-number">
                  TO: #{{ travelOrder.id }}
                </div>
                <travel-order-status-label
                  v-if="isTravelOrderCancelled(travelOrder)"
                  :travel-order="travelOrder"
                  class="ml-2"
                  data-qa="route-travel-order-status"
                ></travel-order-status-label>
              </div>
              <div class="d-flex align-center">
                <div class="text-grey-darken-1 font-size-12 d-flex">
                  <span class="mr-1">REF:</span>
                  <show-more
                    :items="travelOrder.order.refNumbers"
                    data-qa="route-ref-number"
                    item-class="text-grey-darken-1 font-size-12"
                  ></show-more>
                </div>
                <div class="text-grey-darken-2 font-size-12 font-weight-500 ml-4" data-qa="route-rate">
                  {{ travelOrder.rate }}$
                </div>
                <router-link
                  v-if="travelOrder.order.id"
                  :to="{ name: 'order-view', params: { id: travelOrder.order.id } }"
                  class="text-primary font-size-12 font-weight-500 ml-4 text-decoration-none"
                  data-qa="route-order-number"
                  target="_blank"
                >
                  Order: #{{ travelOrder.order.number }}
                </router-link>
                <v-tooltip location="top">
                  <template #activator="{ props }">
                    <v-icon
                      v-if="travelOrder.order.temperatures?.length || travelOrder.order.protectFromFreezing"
                      class="ml-2"
                      color="info"
                      data-qa="route-temperature-control"
                      icon="mdi-snowflake"
                      size="16"
                      v-bind="props"
                    ></v-icon>
                  </template>
                  <template v-if="travelOrder.order.protectFromFreezing">Protect from freezing</template>
                  <template v-else-if="travelOrder.order.temperatures?.length">
                    Temperature control from {{ travelOrder.order.temperatures[0] }} to
                    {{ travelOrder.order.temperatures[1] }} ℉
                  </template>
                </v-tooltip>
              </div>
            </div>
            <div class="mt-3 justify-space-between">
              <div class="font-weight-500 text-grey-darken-3 mb-2" data-qa="route-stop-road">
                {{ getOrderRoute(travelOrder.order) }}
              </div>
              <router-link
                :to="{ name: 'customer-edit', params: { id: travelOrder.order.customer.id } }"
                class="font-size-12 text-uppercase font-weight-500 text-primary text-decoration-none"
                data-qa="route-customer-name"
                target="_blank"
              >
                {{ travelOrder.order.customer.name }}
              </router-link>
            </div>
          </template>
        </div>
      </div>
    </div>

    <alert-message v-if="isPlanned" bg-color="indigo-lighten-5" border-color="none" class="mt-3" icon-color="indigo">
      <span class="text-indigo font-weight-500">
        {{ PlannedRouteMessage }}
      </span>
    </alert-message>

    <replace-truck-dialog
      v-if="showReplaceTruckDialog"
      v-model="showReplaceTruckDialog"
      :current-route-id="route.id"
      :current-route-status="route.status"
      :replaced-truck="route.truck"
      :trip-id="tripId"
      @success="finalizeReplaceTruck"
    />

    <routes-dialog v-if="showTrucksRoutesDialog" v-model="showTrucksRoutesDialog" :routes="trucksRoutes">
      <template #header>
        <div class="text-center">
          <div class="d-flex align-center justify-center text-center font-size-20 font-weight-500 col-gap-1">
            <span>{{ $t("general.route-has-been-updated") }}</span>
          </div>

          <div class="d-flex align-center justify-center font-size-16 mt-4">
            <truck-link :truck-id="replacedTruck.id" :truck-number="replacedTruck.number" class="mr-1" />
            <span class="text-grey-darken-2 font-weight-500"
              >{{ $t("general.was-unassigned-from-route") }}{{ route.id }}</span
            >
          </div>

          <div class="d-flex align-center justify-center font-size-16 mt-1">
            <truck-link :truck-id="assignedTruck.id" :truck-number="assignedTruck.number" class="mr-1" />
            <span class="text-grey-darken-2 font-weight-500"
              >{{ $t("general.was-assigned-to-route") }}{{ route.id }}</span
            >
          </div>

          <p class="text-grey-darken-1 mt-4">
            {{ $t("general.it_affected_empty_miles") }}
            <br />
            <span>{{ $t("general.please-recalculate-or-cancel") }}</span>
          </p>
        </div>
      </template>
    </routes-dialog>

    <rate-confirmation-dialog v-model="showRateConfirmationDialog" :route-id="route.id" />
  </div>
</template>

<style lang="scss" scoped>
.expand-button {
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
  transition: all 250ms ease-in-out;

  &.expanded {
    transform: rotate(180deg);
  }
}

.driver-label {
  font-size: 9px;
  font-weight: 600;
  width: 16px;
  height: 16px;
  line-height: 16px;
  text-align: center;
}
</style>
